import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { apiError, AttendanceExceptionType, type AttendanceException } from './lib';

/**
 * What has to be looked at before a period can be signed off: late arrivals,
 * missing punches the auto-close sweep closed for someone, unscheduled work,
 * no-shows. Defaults to unresolved — the dashboard is the ones nobody has
 * looked at yet, not the archive.
 */
export default function ExceptionsTab() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canResolve = can('attendance.approve');

  const [type, setType] = useState<AttendanceExceptionType | ''>('');
  const [rows, setRows] = useState<AttendanceException[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: AttendanceException[] }>('/attendance/exceptions', {
        params: { limit: 100, type: type || undefined },
      })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('workforce.exceptions.loadFailed'))))
      .finally(() => setLoading(false));
  }, [type, t]);

  useEffect(load, [load]);

  const resolve = async (exc: AttendanceException) => {
    if (!note.trim()) return;
    try {
      await api.post(`/attendance/exceptions/${exc.id}/resolve`, { note: note.trim() });
      toast.success(t('workforce.exceptions.resolved'));
      setResolvingId(null);
      setNote('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('workforce.exceptions.resolveFailed')));
    }
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-4">
          <Field label={t('workforce.exceptions.type')}>
            <Select value={type} onChange={(e) => setType(e.target.value as AttendanceExceptionType | '')}>
              <option value="">{t('workforce.exceptions.anyType')}</option>
              {Object.values(AttendanceExceptionType).map((tpe) => (
                <option key={tpe} value={tpe}>
                  {t(`workforce.exceptionType.${tpe}`)}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('workforce.exceptions.empty')} hint={t('workforce.exceptions.emptyHint')} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('workforce.exceptions.type')}</TH>
                <TH>{t('workforce.exceptions.employee')}</TH>
                <TH>{t('workforce.exceptions.detail')}</TH>
                <TH>{t('workforce.exceptions.when')}</TH>
                {canResolve && <TH />}
              </TR>
            </THead>
            <tbody>
              {rows.map((exc) => (
                <TR key={exc.id}>
                  <TD>
                    <Badge tone="warning">{t(`workforce.exceptionType.${exc.type}`)}</Badge>
                  </TD>
                  <TD>
                    {exc.timeEntry
                      ? `${exc.timeEntry.employee.firstName} ${exc.timeEntry.employee.lastName ?? ''}`
                      : t('workforce.exceptions.noSubject')}
                  </TD>
                  <TD className="text-ink-muted">
                    {exc.detail ?? (exc.magnitude !== null ? String(exc.magnitude) : '—')}
                  </TD>
                  <TD className="text-ink-muted">{formatDateTime(exc.createdAt, i18n.language)}</TD>
                  {canResolve && (
                    <TD className="text-end">
                      {resolvingId === exc.id ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <Input
                            autoFocus
                            className="h-9 w-40"
                            placeholder={t('workforce.exceptions.notePlaceholder')}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                          />
                          <Button size="sm" disabled={!note.trim()} onClick={() => void resolve(exc)}>
                            {t('common.confirm')}
                          </Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setResolvingId(exc.id)}>
                          {t('workforce.exceptions.resolve')}
                        </Button>
                      )}
                    </TD>
                  )}
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </div>
  );
}
